import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Check, Shield, Lock, CreditCard, Star, AlertTriangle, BookOpen, Brain, Eye, Users } from 'lucide-react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchasePopup } from '../components/PurchasePopup';
import { ComboCard, ComboCardProps } from '../components/ComboCard';
import { TestimonialCard } from '../components/TestimonialCard';
// --- DATA ---
const BENEFITS = [{
  icon: <Eye className="w-6 h-6 text-yellow-500" data-id="element-59" />,
  title: 'Detectar mentiras al instante'
}, {
  icon: <Brain className="w-6 h-6 text-yellow-500" data-id="element-60" />,
  title: 'Leer intenciones ocultas'
}, {
  icon: <Users className="w-6 h-6 text-yellow-500" data-id="element-61" />,
  title: 'Dominar dinámicas de grupo'
}, {
  icon: <Shield className="w-6 h-6 text-yellow-500" data-id="element-62" />,
  title: 'Blindarte contra manipulación'
}, {
  icon: <Star className="w-6 h-6 text-yellow-500" data-id="element-63" />,
  title: 'Aumentar tu estatus percibido'
}, {
  icon: <Lock className="w-6 h-6 text-yellow-500" data-id="element-64" />,
  title: 'Negociar con ventaja injusta'
}];
const BONUSES = [{
  title: 'BONUS #1: Manual de Microexpresiones',
  subtitle: 'Lo que la cara no puede ocultar',
  description: 'Aprendé a detectar miedo, culpa, tensión reprimida y señales que duran menos de un segundo.',
  image: 'https://cdn.magicpatterns.com/uploads/nz9x4pRd5VieGvcwi6UD11/ChatGPT_Image_23_feb_2026,_17_27_43.png'
}, {
  title: 'BONUS #2: Control de Conversaciones',
  subtitle: 'Dominá el marco verbal',
  description: 'Redirigí preguntas, evitá quedar en posición débil y ganá discusiones sin elevar la voz.',
  image: 'https://cdn.magicpatterns.com/uploads/s35pdJkTNit7wz2oLyu7LV/ChatGPT_Image_23_feb_2026,_17_28_39.png'
}, {
  title: 'BONUS #3: Defensa Anti-Manipulación',
  subtitle: 'Escudo psicológico total',
  description: 'Gaslighting, victimización y presión social. Aprendé a neutralizar sin confrontaciones.',
  image: 'https://cdn.magicpatterns.com/uploads/tW3jKuCMZyvp4QjoYsAWxD/ChatGPT_Image_23_feb_2026,_17_29_23.png'
}];
const TESTIMONIALS = [{
  name: 'Javier M.',
  role: 'Gerente de Ventas, 34 años',
  content: 'Siempre sentía que en las reuniones me perdía de algo. Con el módulo de jerarquías ocultas entendí quién realmente tomaba las decisiones. Cerré dos tratos grandes la semana pasada aplicando esto.'
}, {
  name: 'Sofía L.',
  role: 'Abogada, 29 años',
  content: 'La sección de microexpresiones es brutal. Pude detectar una mentira clave en una negociación extrajudicial solo observando la tensión en la mandíbula del oponente. Se pagó solo.'
}, {
  name: 'Marcos D.',
  role: 'Emprendedor, 41 años',
  content: 'No es motivación barata. Es un sistema técnico. Lo uso para contratar personal y para negociar con proveedores. La ventaja que te da saber lo que el otro piensa es injusta.'
}, {
  name: 'Valentina R.',
  role: 'Estudiante de Psicología, 22 años',
  content: 'En la facultad te enseñan teoría. Esto es práctica pura. Los códigos de comportamiento en grupos me cambiaron la forma de ver mis relaciones sociales.'
}, {
  name: 'Esteban K.',
  role: 'Team Leader, 38 años',
  content: 'El manual de influencia aplicada es oro. Logré que mi equipo adoptara un cambio difícil sin resistencia, usando solo los principios de autoridad percibida.'
}];
// COMBO_DATA without onBuy — handlers are attached inside the component
const COMBO_DATA = [{
  title: 'Combo 1: Sistema Base',
  price: '19.990',
  originalPrice: '35.000',
  discount: '45%',
  description: 'Para iniciarse en la lectura de comportamiento.',
  image: 'https://cdn.magicpatterns.com/uploads/ix8jBM19jch6yWRvzNPZ3J/ChatGPT_Image_23_feb_2026,_17_24_01.png',
  features: ['Manual Principal: Códigos Ocultos (60 módulos)', 'Bonus: Microexpresiones Reales', 'Bonus: Control de Conversaciones', 'Bonus: Defensa Contra Manipulación', 'Acceso de por vida', 'Actualizaciones futuras', '📄 Material digital (PDF) — enviado por mail automáticamente'],
  link: 'https://checkout7.rosetadigital.com/cart/46233839665323:1',
  comboName: 'Combo 1 - Sistema Base',
  comboPrice: 19990
}, {
  title: 'Combo 2: El Más Elegido',
  price: '29.990',
  originalPrice: '55.000',
  discount: '55%',
  description: 'El equilibrio perfecto entre detección e influencia.',
  isRecommended: true,
  image: 'https://cdn.magicpatterns.com/uploads/7R2NKk3S7EaYqmJ113mCfR/ChatGPT_Image_23_feb_2026,_17_41_43.png',
  features: ['TODO lo del Combo 1', 'Manual 2: Influencia Psicológica Aplicada', '📄 Material digital (PDF) — enviado por mail automáticamente'],
  link: 'https://checkout7.rosetadigital.com/cart/46233866698923:1',
  comboName: 'Combo 2 - El Más Elegido',
  comboPrice: 29990
}, {
  title: 'Combo 3: Nivel Avanzado',
  price: '39.990',
  originalPrice: '80.000',
  discount: '60%',
  description: 'Dominio total de la estrategia social.',
  isPremium: true,
  image: 'https://cdn.magicpatterns.com/uploads/ro9p2fiXLDjxaKTyDDd6Nw/ChatGPT_Image_23_feb_2026,_17_48_35.png',
  features: ['TODO lo del Combo 1 y Combo 2', 'Manual 3: Estrategia Social Avanzada', '📄 Material digital (PDF) — enviado por mail automáticamente'],
  link: 'https://checkout7.rosetadigital.com/cart/46233874071723:1',
  comboName: 'Combo 3 - Nivel Avanzado',
  comboPrice: 39990
}];
export function LandingPage() {
  useEffect(() => {
    document.title = 'Códigos Ocultos del Comportamiento Humano';
    // OG meta tags for link previews
    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('og:title', 'Códigos Ocultos del Comportamiento Humano');
    setMeta('og:description', 'El sistema estructurado para leer intenciones, detectar mentiras y blindarte contra la manipulación moderna.');
    setMeta('og:image', 'https://cdn.magicpatterns.com/uploads/7iFacd7iByn45jgEcPqVyc/ChatGPT_Image_23_feb_2026,_17_24_01.png');
    setMeta('og:type', 'website');
  }, []);
  // Pixel initialized globally in index.tsx
  const handleBuy = (link: string, comboName: string, price: number) => {
    try {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        value: price,
        currency: 'ARS',
        content_name: comboName
      });
    } catch (e) {}
    window.location.href = link;
  };
  // Build COMBOS with onBuy inside component so handleBuy is in scope
  const COMBOS: ComboCardProps[] = COMBO_DATA.map(c => ({
    ...c,
    onBuy: () => handleBuy(c.link, c.comboName, c.comboPrice)
  }));
  const scrollToCheckout = () => {
    const element = document.getElementById('pricing');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-black text-gray-300 font-sans selection:bg-yellow-500 selection:text-black overflow-x-hidden" data-id="element-65">
      <UrgencyBar data-id="element-66" />
      <PurchasePopup data-id="element-67" />

      {/* --- HERO SECTION --- */}
      <section className="relative pt-24 pb-16 px-4 md:pt-32 md:pb-24 overflow-hidden" data-id="element-68">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-yellow-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-69" />

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-70">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-71">
            <span className="inline-block py-1 px-3 rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-500 font-bold text-xs md:text-sm uppercase tracking-widest mb-6" data-id="element-72">
              Nadie te preparó para esto...
            </span>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 uppercase tracking-tight" data-id="element-73">
              Códigos Ocultos del <br data-id="element-74" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600" data-id="element-75">
                Comportamiento Humano
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-76">
              El sistema estructurado para leer intenciones, detectar mentiras y
              blindarte contra la manipulación moderna. Dejá de adivinar, empezá
              a saber.
            </p>

            <div className="flex flex-col items-center gap-6" data-id="element-77">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={scrollToCheckout} className="bg-yellow-500 text-black font-black text-lg py-4 px-8 rounded-full shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.6)] transition-all flex items-center gap-2 uppercase tracking-wide" data-id="element-78">
                Quiero Acceso Inmediato{' '}
                <ArrowDown className="w-5 h-5 animate-bounce" data-id="element-79" />
              </motion.button>

              <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-80">
                <div className="flex -space-x-2" data-id="element-81">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-6 h-6 rounded-full bg-zinc-800 border border-black flex items-center justify-center text-[8px] text-white" data-id="element-82">
                      <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" data-id="element-83" />
                    </div>)}
                </div>
                <span data-id="element-84">+1,200 estudiantes activos</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="mt-16 relative mx-auto max-w-lg" data-id="element-85">
            <div className="absolute inset-0 bg-yellow-500/20 blur-2xl -z-10 rounded-full" data-id="element-86" />
            <img src="https://cdn.magicpatterns.com/uploads/7iFacd7iByn45jgEcPqVyc/ChatGPT_Image_23_feb_2026,_17_24_01.png" alt="Códigos Ocultos Book Cover" className="w-full h-auto rounded-lg shadow-2xl border border-zinc-800 hover:shadow-[0_0_30px_rgba(234,179,8,0.2)] transition-shadow duration-500" data-id="element-87" />
          </motion.div>
        </div>
      </section>

      {/* --- SALES LETTER --- */}
      <section className="py-20 px-4 bg-zinc-950" data-id="element-88">
        <div className="max-w-2xl mx-auto" data-id="element-89">
          <div className="prose prose-invert prose-lg prose-p:text-gray-300 prose-headings:font-black prose-headings:text-white prose-strong:text-white prose-strong:font-bold" data-id="element-90">
            <h2 className="text-3xl md:text-4xl text-center mb-12 uppercase tracking-tight" data-id="element-91">
              Hay códigos que la mayoría{' '}
              <span className="text-yellow-500" data-id="element-92">no conoce</span>
            </h2>

            <p data-id="element-93">
              Y no porque sean imposibles de entender. Sino porque nadie los
              explica de forma estructurada.
            </p>
            <p data-id="element-94">Cada conversación. Cada reunión. Cada interacción social.</p>
            <p className="font-bold text-white text-xl" data-id="element-95">
              Está llena de señales invisibles.
            </p>

            <ul className="list-none pl-0 space-y-2 my-8" data-id="element-96">
              {['Microincongruencias', 'Cambios de energía', 'Jerarquías implícitas', 'Pruebas de poder', 'Intentos de manipulación sutil'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-97">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-98" />
                  {item}
                </li>)}
            </ul>

            <p data-id="element-99">
              El problema no es que no existan.{' '}
              <strong data-id="element-100">El problema es que la mayoría no sabe leerlas.</strong>
            </p>

            <div className="my-12 pl-6 border-l-4 border-yellow-500 italic text-gray-400" data-id="element-101">
              "Sentir que algo no cerraba… pero no saber exactamente qué.
              Quedarte pensando después de una conversación: 'Había algo raro'."
            </div>

            <p data-id="element-102">Eso genera inseguridad. Y la inseguridad genera desventaja.</p>
            <p data-id="element-103">
              No es falta de inteligencia. <strong data-id="element-104">Es falta de sistema.</strong>
            </p>

            {/* THE DISCOVERY BOX */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-xl my-12 shadow-xl relative overflow-hidden" data-id="element-105">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent" data-id="element-106" />
              <h3 className="text-2xl font-black text-white mb-4 flex items-center gap-2" data-id="element-107">
                <BookOpen className="text-yellow-500" data-id="element-108" />
                EL DESCUBRIMIENTO
              </h3>
              <p className="mb-4" data-id="element-109">
                El comportamiento humano sigue patrones. Tiene estructuras
                repetibles. Se manifiesta en microseñales observables.
              </p>
              <p className="font-bold text-white" data-id="element-110">
                Cuando aprendés a identificarlas, dejás de adivinar… y empezás a
                interpretar con claridad.
              </p>
            </div>

            <p data-id="element-111">
              Presentamos:{' '}
              <strong data-id="element-112">CÓDIGOS OCULTOS DEL COMPORTAMIENTO HUMANO</strong>.
            </p>
            <p data-id="element-113">
              Un sistema completo, dividido en niveles progresivos, diseñado
              para que puedas detectar la intención real, identificar mentiras y
              tomar el control.
            </p>
          </div>
        </div>
      </section>

      {/* --- BENEFITS GRID --- */}
      <section className="py-20 px-4 bg-black border-t border-zinc-900" data-id="element-114">
        <div className="max-w-6xl mx-auto" data-id="element-115">
          <div className="text-center mb-16" data-id="element-116">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 uppercase" data-id="element-117">
              Qué va a cambiar en vos
            </h2>
            <p className="text-gray-500" data-id="element-118">
              Resultados tangibles de aplicar el sistema
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-119">
            {BENEFITS.map((benefit, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-xl hover:bg-zinc-900 transition-colors" data-id="element-120">
                <div className="mb-4" data-id="element-121">{benefit.icon}</div>
                <h3 className="text-lg font-bold text-white" data-id="element-122">
                  {benefit.title}
                </h3>
              </motion.div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-123">
            <div className="inline-block bg-zinc-900 border border-zinc-800 px-6 py-3 rounded-full" data-id="element-124">
              <p className="text-gray-400 font-medium" data-id="element-125">
                "Esto no es motivación, es{' '}
                <span className="text-yellow-500 font-bold" data-id="element-126">
                  entrenamiento técnico
                </span>
                ."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- BONUSES --- */}
      <section className="py-20 px-4 bg-zinc-950 relative overflow-hidden" data-id="element-127">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-yellow-500/5 blur-[150px] pointer-events-none" data-id="element-128" />

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-129">
          <div className="flex flex-col items-center mb-16" data-id="element-130">
            <span className="bg-red-600 text-white text-xs font-black px-3 py-1 rounded uppercase tracking-widest mb-4 animate-pulse" data-id="element-131">
              Gratis solo por hoy
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white text-center uppercase" data-id="element-132">
              Bonus Exclusivos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-133">
            {BONUSES.map((bonus, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2
          }} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden group hover:border-yellow-500/30 transition-all" data-id="element-134">
                <div className="h-48 overflow-hidden" data-id="element-135">
                  <img src={bonus.image} alt={bonus.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-136" />
                </div>
                <div className="p-6" data-id="element-137">
                  <h3 className="text-yellow-500 font-black text-lg mb-1 uppercase" data-id="element-138">
                    {bonus.title}
                  </h3>
                  <h4 className="text-white font-bold mb-3" data-id="element-139">
                    {bonus.subtitle}
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-140">{bonus.description}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* --- TRUST BADGE --- */}
      <section className="py-12 bg-white flex justify-center items-center" data-id="element-141">
        <div className="flex flex-col items-center gap-4" data-id="element-142">
          <p className="text-gray-500 text-sm font-bold uppercase tracking-widest" data-id="element-143">
            Pago 100% seguro procesado por:
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/nGCSJF7QXtxUjG21UcCEvv/pasted-image.png" alt="Mercado Pago" className="h-16 w-auto object-contain" data-id="element-144" />
        </div>
      </section>

      {/* --- PRICING / CHECKOUT --- */}
      <section id="pricing" className="py-24 px-4 bg-black relative" data-id="element-145">
        <div className="max-w-7xl mx-auto" data-id="element-146">
          <div className="text-center mb-16" data-id="element-147">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase" data-id="element-148">
              Elige tu Nivel
            </h2>
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 px-4 py-2 rounded text-red-500 font-bold animate-pulse" data-id="element-149">
              <AlertTriangle className="w-4 h-4" data-id="element-150" />
              <span data-id="element-151">Solo quedan 47 copias disponibles</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-152">
            {COMBOS.map((combo, idx) => <ComboCard key={idx} {...combo} data-id="element-153" />)}
          </div>

          <div className="mt-16 flex justify-center gap-8 text-gray-500 text-sm font-medium" data-id="element-154">
            <div className="flex items-center gap-2" data-id="element-155">
              <Shield className="w-4 h-4 text-yellow-500" data-id="element-156" />
              Garantía de 7 días
            </div>
            <div className="flex items-center gap-2" data-id="element-157">
              <Lock className="w-4 h-4 text-yellow-500" data-id="element-158" />
              Pago Encriptado
            </div>
            <div className="flex items-center gap-2" data-id="element-159">
              <Check className="w-4 h-4 text-yellow-500" data-id="element-160" />
              Acceso Inmediato
            </div>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS --- */}
      <section className="py-20 px-4 bg-zinc-950" data-id="element-161">
        <div className="max-w-6xl mx-auto" data-id="element-162">
          <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-16 uppercase" data-id="element-163">
            Lo que dicen los estudiantes
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-164">
            {TESTIMONIALS.map((testimonial, idx) => <TestimonialCard key={idx} {...testimonial} data-id="element-165" />)}
          </div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="py-12 bg-black border-t border-zinc-900 text-center" data-id="element-166">
        <div className="max-w-4xl mx-auto px-4" data-id="element-167">
          <h3 className="text-xl font-black text-white mb-4 uppercase" data-id="element-168">
            Códigos Ocultos
          </h3>
          <p className="text-gray-500 text-sm mb-8" data-id="element-169">
            Este producto no garantiza la obtención de resultados. Cualquier
            referencia al desempeño de una estrategia no debe ser interpretada
            como una garantía de resultados.
          </p>
          <div className="flex justify-center gap-6 text-sm text-gray-600" data-id="element-170">
            <a href="#" className="hover:text-yellow-500 transition-colors" data-id="element-171">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-yellow-500 transition-colors" data-id="element-172">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-yellow-500 transition-colors" data-id="element-173">
              Contacto
            </a>
          </div>
          <p className="mt-8 text-xs text-zinc-800" data-id="element-174">
            © 2024 Códigos Ocultos. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}